/**
 * Date-string helpers for the continuous-scroll schedule grid (ff-feedback #9).
 *
 * The grid's columns are 'YYYY-MM-DD' strings, not day-of-month integers. That
 * swap is the whole of item 9: an integer needs a month to mean anything, so
 * every consumer had to rebuild `${year}-${month}-${day}`, and a window that
 * crosses a month boundary has no single month to rebuild from.
 *
 * All of these parse LOCAL time deliberately. `new Date('2026-08-05')` is parsed
 * as UTC midnight per spec, which lands on the 4th for anyone west of Greenwich —
 * so the grid would highlight the wrong "today" and mislabel weekends.
 */

const parts = (ds: string): [number, number, number] => {
  const [y, m, d] = ds.split('-').map(Number);
  return [y, m, d];
};

/** Local Date at midnight for a 'YYYY-MM-DD' string. */
export const localDate = (ds: string): Date => {
  const [y, m, d] = parts(ds);
  return new Date(y, m - 1, d);
};

/** 0 = Sunday. */
export const dayOfWeek = (ds: string): number => localDate(ds).getDay();

export const isWeekend = (ds: string): boolean => {
  const dow = dayOfWeek(ds);
  return dow === 0 || dow === 6;
};

/** Day of month, for the number printed in the column header. */
export const dayOfMonth = (ds: string): number => parts(ds)[2];

/** True on the 1st — where the grid draws a month separator. */
export const isMonthStart = (ds: string): boolean => parts(ds)[2] === 1;

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "Aug" normally, "Jan 2027" when the year turns — a sticky label has to say
 *  which January it is when the window spans New Year. */
export const monthLabel = (ds: string, includeYear = false): string => {
  const [y, m] = parts(ds);
  return includeYear ? `${MONTH_ABBR[m - 1]} ${y}` : MONTH_ABBR[m - 1];
};

/** Today as 'YYYY-MM-DD' in local time. */
export const todayYmd = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Split a run of days into per-month groups, for the sticky month header row.
 * Returns the first day and the column count of each month in the window, so the
 * header can size its labels off the same array the body renders from.
 */
export const monthRuns = (days: string[]): Array<{ start: string; count: number }> => {
  const runs: Array<{ start: string; count: number }> = [];
  for (const ds of days) {
    const last = runs[runs.length - 1];
    if (last && ds.slice(0, 7) === last.start.slice(0, 7)) last.count++;
    else runs.push({ start: ds, count: 1 });
  }
  return runs;
};
